<template>
	<SignUpPageBase :content="this">
		<h4 class="font_lg font_bold margin_title_bottom">{{ $t("signUp.representatives.title") }}</h4>
		<span>{{ $t("signUp.representatives.description") }}</span>
		<div class="flex flex_center flex_column w_100">
			<div v-for="(item, index) in form.representatives">
				<div class="margin_top">
					<div class="flex flex_between">
						<span class ="font_bold w_100">{{ $t("signUp.representatives.entry.title", { index: index + 1 }) }}</span>
						<div class="close_box pointer" @click="removeRepresentative(index)" v-if="form.representatives.length > minRepresentatives">
							<i class="el-dialog__close el-icon el-icon-close font_lg"></i>
						</div>
					</div>
					<SignUpRepresentativeComponent
						:info="item"
						@changed="(info) => representativeChanged(index, info)"
						ref="representative"
					></SignUpRepresentativeComponent>
				</div>
			</div>
			<div class="margin_top w_100">
				<md-button class="md-primary" v-if="form.representatives.length < maxRepresentatives" @click="addRepresentative">
					{{ $t("signUp.representatives.button.add") }}
				</md-button>
			</div>
		</div>
	</SignUpPageBase>
</template>

<script>

import SignUpRepresentativeComponent from "@/views/register/SignUpRepresentativeComponent";
import SignUpPageBase from "@/views/register/SignUpPageBase";
import {mapGetters} from "vuex";
import {localization} from "@/common/lang/localization";

export default {
	components: {
		SignUpPageBase,
		SignUpRepresentativeComponent
	},
	data() {
		return {
			representativeDefaults: {
				firstName: "",
				lastName: "",
				email: "",
				birthday: "",
				language: ""
			},
			form: {
				representatives: [],
			},
			maxRepresentatives: 4,
			minRepresentatives: 2,

			// Just a circular counter changed every time representative data is modified to force an update of the form validation indicator
			// because vue does not pick up on the change automatically.
			enforceValidationKey: 0,
		};
	},
	created() {
		this.form.representatives = this.signUpData.representatives || [];

		this.maxRepresentatives = this.signUpData.hasSolePowerOfRepresentation ? 1 : 4;
		this.minRepresentatives = this.signUpData.hasSolePowerOfRepresentation ? 1 : 2;

		if (this.form.representatives.length === 0) { // First time we come to this page

			// If master user is a representative we add his information as first representative
			if (this.signUpData.isMasterUserRepresentative) {
				this.addRepresentative(this.buildRepresentativeFromMasterUserData());
			}

			// Fill up the list with empty representatives until we have the minimum of entries required
			for (let i = this.form.representatives.length; i < this.minRepresentatives; i++) {
				this.addRepresentative({ ...this.representativeDefaults });
			}
		} else { // We already have representative data stored from a previous time

			// If we already have the master user saved as representative but with the new conditions this is no longer true then we clean up this entry
			// or add it if required
			let representativeMatchingMasterUserIndex = this.form.representatives.findIndex(entry => this.doesDataMatchWithMasterUser(entry));
			let representativeMatchingMasterUser = representativeMatchingMasterUserIndex > -1 ? this.form.representatives[representativeMatchingMasterUserIndex] : null;

			if (representativeMatchingMasterUser && !this.signUpData.isMasterUserRepresentative) {
				this.removeRepresentative(representativeMatchingMasterUserIndex);
			} else if (!representativeMatchingMasterUser && this.signUpData.isMasterUserRepresentative) {
				this.addRepresentative(this.buildRepresentativeFromMasterUserData(), 0);
			}

			// Remove entries if we have too many based on the new conditions
			for (let i = this.form.representatives.length; i > this.maxRepresentatives; i--) {
				this.removeRepresentative(i - 1);
			}

			// Fill up the list with empty representatives until we have the minimum of entries required
			for (let i = this.form.representatives.length; i < this.minRepresentatives; i++) {
				this.addRepresentative({ ...this.representativeDefaults });
			}
		}
	},
	computed: {
		...mapGetters({
			signUpData: "local/signUpData"
		}),
		useCustomFormValidation() {
			return true;
		},
		isFormValid() {
			// This must be mentioned here for the validation to be enforced on changes
			this.enforceValidationKey;

			return this.$refs.representative
				? this.$refs.representative.every(rep => rep.isFormValid)
				: false;
		}
	},
	methods: {
		doesDataMatchWithMasterUser(data) {
			return data.firstName === this.signUpData.userFirstName && data.lastName === this.signUpData.userLastName && data.email === this.signUpData.userEmail;
		},
		buildRepresentativeFromMasterUserData() {
			return {
				firstName: this.signUpData.userFirstName,
				lastName: this.signUpData.userLastName,
				email: this.signUpData.userEmail,
				birthday: "",
				language: this.signUpData.language
			};
		},
		representativeChanged(index, data) {
			this.form.representatives[index] = data;

			this.enforceValidation();
		},
		removeRepresentative(index) {
			this.form.representatives.splice(index, 1);
			this.enforceValidation();
		},
		addRepresentative(info, index = null) {
			if (index != null) {
				this.form.representatives.splice(0, 0, info);
			} else {
				this.form.representatives.push(info);
			}
			this.enforceValidation();
		},
		validateForm() {
        if (!this.validateEmails()) {
          return Promise.reject(this.$t("signUp.representatives.validation.duplicateEmail"));
        }
			return Promise.all(this.$refs.representative.map(item => item.validateForm()));
		},
    validateEmails() {
      let isValid = true;
      const list = this.$refs.representative.map(item => item.form.email);
      const set = new Set(list)
      if (list.length !== set.size) {
        isValid = false;
      }
      return isValid;
    },
		enforceValidation() {
			this.$nextTick(() => this.enforceValidationKey = (this.enforceValidationKey + 1) % 2);
		}
	},
};
</script>